/**
 * Add/Edit Staff dialog (DNKADM-0053 U1) — a MUI `Dialog` that creates/updates a
 * `v2_staff` entity via `createStaff` / `updateStaff` and round-trips its
 * **location assignments** via `getStaffLocations` / `setStaffLocations` (handoff
 * §5.6).
 *
 * Fields: **first name** + **last name** (required — unique on
 * `(tenant, firstName, lastName)`), **email** + **phone** (optional), a
 * **multi-select of the tenant's active owned locations** (the locations the staffer
 * can be scheduled at), and the **Login** picker below. `isActive` is NOT set here —
 * activation is the list's deactivate/reactivate affordance (§4.3).
 *
 * **Login (DNKADM-0362, DNKAPI-0520 / handoff §5).** A coach record links to the
 * person's staff login — `authUserId`, the `userId` of a Team & access roster row with
 * `kind: 'STAFF'`. It replaces the retired CRM-contact id outright: staff are never in
 * the CRM, so there is nothing here to preserve. The picker offers staff rows only (the
 * server rejects anything else with `400 STAFF_LINK_NOT_IN_ROSTER`) plus "Not linked",
 * and the roster comes from the caller when it already loaded one (the detail screen)
 * or from one `getPlatformMembers()` per dialog open otherwise — the Staff screen is
 * under `RequireAdmin`, so the reader is authorised. The two link failures render on
 * the FIELD, not as a toast, and leave the rest of the form intact.
 *
 * On EDIT the link is ALWAYS sent with the dialog's current value (DNKADM-0392): the
 * linked `userId`, or `null` for "Not linked". The key is never omitted, because
 * dink-api's staff PUT applies `authUserId` from the request unconditionally and
 * Jackson binds an absent key and an explicit `null` identically — an update body
 * without the key silently UNLINKS the coach (the DNKADM-0362 "send it only when it
 * changed" omission did exactly that on every name/phone edit). On CREATE a chosen
 * login sends its id and "Not linked" sends no key (nothing to clear on a new row).
 *
 * On create: POST the staff, then (if any locations were picked) PUT the assignments
 * for the new id. On edit: PUT the staff, then PUT the assignments if they changed.
 * A validation `400` / `409` renders INLINE; anything else raises an error toast
 * (`classifyFormError`). The active-locations list loads through `<PickListLoader>`
 * is not used here (a plain `useResource` on `listLocations` suffices — an empty
 * tenant simply shows no options with a helper note).
 *
 * **Embedded in a drawer (DNKADM-0494).** {@link StaffFields} renders the same form for one
 * Staff-page card, with no dialog chrome: `basics` (name, email, phone, Login) or `where`
 * (assigned locations). The card's drawer owns the one Save and drives it through
 * `handleRef`. Save is the dialog's own edit save, unchanged: the staff PUT (every field and
 * the login link, as loaded or as edited), then the assignments PUT only when they changed.
 * A failure that would have raised a toast shows inline instead; the drawer raises the
 * success toast.
 */
import { useEffect, useId, useImperativeHandle, useState, type Ref } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createStaff,
  getStaffLocations,
  setStaffLocations,
  updateStaff,
  type StaffInput,
  type V2Staff,
} from '@/api/staff';
import { listLocations, type V2Location } from '@/api/locations';
import { getPlatformMembers } from '@/api/platformMembers';
import type { ShowToast } from '@/components/toastContext';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { staffLinkErrorMessage } from './staffError';
import { classifyFormError } from '@/api/screenError';
import { toStaffLoginOptions, type StaffLoginOption } from './staffLogins';
import { PhoneField } from '@/components/phone/PhoneField';
import type { StaffEditorHandle } from './staffEditorHandle';

export interface StaffDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The staff member being edited, or `null` to add a new one. */
  staff: V2Staff | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
  /**
   * The staff-login options the CALLER already loaded (the detail screen resolves the
   * same roster for its header). Passed ⇒ the dialog reuses them and does NOT fetch the
   * roster a second time; omitted ⇒ it loads its own once per open.
   */
  loginOptions?: StaffLoginOption[] | undefined;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
}

/** The `<Select>` value standing for "no linked login" (never a real `userId`). */
const NOT_LINKED = 'none';

export function StaffDialog(props: StaffDialogProps) {
  // Remount the inner form per open / per edited staff so its inputs reseed.
  if (!props.open) return null;
  return <StaffDialogForm key={props.staff?.id ?? 'new'} {...props} />;
}

/** The Staff-page card a {@link StaffFields} slice belongs to (DNKADM-0494). */
export type StaffFieldsSection = 'basics' | 'where';

export interface StaffFieldsProps {
  /** The loaded staff record (embedded mode always edits). */
  staff: V2Staff;
  section: StaffFieldsSection;
  handleRef: Ref<StaffEditorHandle>;
  showToast: ShowToast;
  /** As on {@link StaffDialogProps}: reuse the caller's roster, no second fetch. */
  loginOptions?: StaffLoginOption[] | undefined;
}

/**
 * The staff form for one card's drawer (DNKADM-0494): only that card's fields, no dialog
 * chrome, saved through `handleRef`.
 */
export function StaffFields({ staff, section, handleRef, showToast, loginOptions }: StaffFieldsProps) {
  return (
    <StaffDialogForm
      staff={staff}
      onClose={() => undefined}
      onSaved={() => undefined}
      showToast={showToast}
      loginOptions={loginOptions}
      embedded={{ handleRef, section }}
    />
  );
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function StaffDialogForm({
  staff,
  onClose,
  onSaved,
  showToast,
  loginOptions: providedLoginOptions,
  embedded,
}: Omit<StaffDialogProps, 'open'> & {
  embedded?: { handleRef: Ref<StaffEditorHandle>; section: StaffFieldsSection };
}) {
  const ids = useId();
  const isEdit = staff !== null;

  const [firstName, setFirstName] = useState(staff?.firstName ?? '');
  const [lastName, setLastName] = useState(staff?.lastName ?? '');
  const [email, setEmail] = useState(staff?.email ?? '');
  const [phone, setPhone] = useState(staff?.phone ?? '');

  // The staff login this coach record belongs to (DNKADM-0362), seeded from what the
  // server already has. On edit the CURRENT selection is always sent — see `validate`.
  const initialAuthUserId = staff?.authUserId ?? null;
  const [authUserId, setAuthUserId] = useState<number | null>(initialAuthUserId);
  const [loginError, setLoginError] = useState<string | null>(null);
  // The roster we fetched ourselves; `null` until it resolves. When the caller supplied
  // options we never fetch, so this stays null and the derived values below use theirs.
  const [fetchedLoginOptions, setFetchedLoginOptions] = useState<StaffLoginOption[] | null>(
    null,
  );
  const loginOptions = providedLoginOptions ?? fetchedLoginOptions ?? [];
  const loginOptionsReady =
    providedLoginOptions !== undefined || fetchedLoginOptions !== null;

  // The tenant's active owned locations (the assignable set) + the picked ids.
  const [locations, setLocations] = useState<V2Location[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  // The assignments as loaded for an edit (to detect a change and skip a no-op PUT).
  const [initialLocationIds, setInitialLocationIds] = useState<string[] | null>(
    isEdit ? null : [],
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The plan-limit popup payload (DNKADM-0065) when a create is capped; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  // Load the active owned locations once; on edit, also load the current assignments.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [locs, assigned] = await Promise.all([
          listLocations({ status: 'active' }),
          isEdit && staff ? getStaffLocations(staff.id) : Promise.resolve<string[]>([]),
        ]);
        if (cancelled) return;
        setLocations(locs);
        // Keep only assignments that are still active+known (a stale id is dropped
        // from the control but won't be re-sent).
        const known = new Set(locs.map((l) => l.id));
        const kept = assigned.filter((id) => known.has(id));
        setSelectedLocationIds(kept);
        setInitialLocationIds(assigned);
      } catch {
        if (cancelled) return;
        // A locations/assignments load failure is non-fatal: the staff fields still
        // save; the assignment control just shows no options + a note.
        setLocations([]);
        setInitialLocationIds(isEdit ? [] : []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, staff]);

  // Load the staff-login roster once per open — unless the caller already has it.
  // Kept OUT of the locations effect above so a roster failure can't blank the
  // location picker (and vice versa); each degrades on its own.
  useEffect(() => {
    if (providedLoginOptions !== undefined) return;
    let cancelled = false;
    void (async () => {
      try {
        const roster = await getPlatformMembers();
        if (!cancelled) setFetchedLoginOptions(toStaffLoginOptions(roster.members));
      } catch {
        // Non-fatal: the staff fields still save; the picker just offers "Not linked"
        // plus a note. NEVER a fallback that offers unfiltered rows.
        if (!cancelled) setFetchedLoginOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providedLoginOptions]);

  function validate(): StaffInput | null {
    const errors: FieldErrors = {};
    const tFirst = firstName.trim();
    const tLast = lastName.trim();
    if (tFirst === '') errors.firstName = 'Enter a first name.';
    if (tLast === '') errors.lastName = 'Enter a last name.';
    if (errors.firstName || errors.lastName) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: StaffInput = { firstName: tFirst, lastName: tLast };
    const e = optional(email);
    if (e !== undefined) input.email = e;
    const p = optional(phone);
    if (p !== undefined) input.phone = p;
    // The staff-login link (DNKADM-0362 / DNKADM-0392). On EDIT always send the current
    // value — the linked id, or an explicit `null` for "Not linked" — never omit the
    // key: the server applies `authUserId` from the request unconditionally, so an
    // absent key reads as `null` and would clear the link on every unrelated edit.
    // On CREATE send it only when a login was chosen (there is nothing to clear).
    if (isEdit || authUserId !== null) input.authUserId = authUserId;
    return input;
  }

  /** Whether the location selection differs from what was loaded (skip a no-op PUT). */
  function locationsChanged(): boolean {
    if (initialLocationIds === null) return false; // assignments not loaded yet
    if (initialLocationIds.length !== selectedLocationIds.length) return true;
    const initial = new Set(initialLocationIds);
    return selectedLocationIds.some((id) => !initial.has(id));
  }

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    setLoginError(null);
    const input = validate();
    if (!input) return false;
    setSaving(true);
    try {
      const saved =
        isEdit && staff ? await updateStaff(staff.id, input) : await createStaff(input);
      const id = saved?.id ?? staff?.id ?? null;
      // Persist the location assignments when they changed (or always on create with
      // a non-empty selection), once we have the staff id.
      if (id !== null && (locationsChanged() || (!isEdit && selectedLocationIds.length > 0))) {
        await setStaffLocations(id, selectedLocationIds);
      }
      // DNKADM-0320: confirm the save (after every write resolves; one toast per action).
      // Embedded: the drawer raises the one success toast.
      if (!embedded) showToast(isEdit ? 'Staff member saved' : 'Staff member added', 'success');
      onSaved();
      return true;
    } catch (err) {
      // A staff-login link failure belongs to ONE field (DNKADM-0362): render it on the
      // Login picker and leave every other value the operator typed exactly as it is —
      // they pick a different login and save again.
      const linkMessage = staffLinkErrorMessage(err);
      if (linkMessage !== null) {
        setLoginError(linkMessage);
        return false;
      }
      // Plan-limit enforcement is Add-only (DNKADM-0065 / DNKAPI-0139) — Edit is
      // never blocked, so only a create maps these codes.
      if (!isEdit) {
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — show the upgrade popup (NOT a generic toast).
          setPlanLimit(parsePlanLimit(err));
          return false;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup); the
          // admin can resubmit (the create may well be under the cap).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return false;
        }
      }
      const view = classifyFormError(
        err,
        'The staff member couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline' || embedded) setServerError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(embedded?.handleRef, () => ({ save: handleSave }));

  const titleId = `${ids}-title`;
  const locationsLabelId = `${ids}-locations-label`;
  const loginLabelId = `${ids}-login-label`;
  // A linked login that is no longer on the roster still has to be selectable-as-is,
  // or opening the dialog would silently re-point the coach at "Not linked".
  const selectedLoginIsKnown =
    authUserId === null || loginOptions.some((option) => option.userId === authUserId);

  const nameFields = (
    <>
          <Stack direction="row" spacing={2}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving}
              size="small"
              required
              fullWidth
              error={Boolean(fieldErrors.firstName)}
              helperText={fieldErrors.firstName}
              slotProps={{ htmlInput: { 'aria-label': 'First name' } }}
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving}
              size="small"
              required
              fullWidth
              error={Boolean(fieldErrors.lastName)}
              helperText={fieldErrors.lastName}
              slotProps={{ htmlInput: { 'aria-label': 'Last name' } }}
            />
          </Stack>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saving}
            size="small"
            slotProps={{ htmlInput: { 'aria-label': 'Email' } }}
          />
    </>
  );
  const contactFields = (
    <>
          <PhoneField
            label="Phone"
            value={phone}
            onChange={setPhone}
            disabled={saving}
            aria-label="Phone"
          />
    </>
  );
  const locationFields = (
    <>
          <FormControl size="small" fullWidth disabled={saving || locations.length === 0}>
            <InputLabel id={locationsLabelId}>Assigned locations</InputLabel>
            <Select
              labelId={locationsLabelId}
              multiple
              value={selectedLocationIds}
              onChange={(e) =>
                setSelectedLocationIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
                )
              }
              input={<OutlinedInput label="Assigned locations" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const loc = locations.find((l) => l.id === id);
                    return (
                      <Chip key={id} size="small" label={loc ? loc.name || loc.id : id} />
                    );
                  })}
                </Box>
              )}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  <Checkbox checked={selectedLocationIds.includes(loc.id)} size="small" />
                  <ListItemText primary={loc.name === '' ? loc.id : loc.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {locations.length === 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1.5 }}>
              No active locations to assign yet. Add a location first, then assign this staff
              member to it.
            </Typography>
          )}
    </>
  );
  const loginField = (
    <>
          {/* The staff login this coach record belongs to (DNKADM-0362). */}
          <FormControl size="small" fullWidth disabled={saving} error={loginError !== null}>
            <InputLabel id={loginLabelId}>Login</InputLabel>
            <Select
              labelId={loginLabelId}
              value={authUserId === null ? NOT_LINKED : String(authUserId)}
              onChange={(e) => {
                const value = e.target.value;
                setAuthUserId(value === NOT_LINKED ? null : Number(value));
                setLoginError(null);
              }}
              input={<OutlinedInput label="Login" />}
            >
              <MenuItem value={NOT_LINKED}>Not linked</MenuItem>
              {!selectedLoginIsKnown && authUserId !== null && (
                <MenuItem value={String(authUserId)}>
                  {loginOptionsReady ? 'Linked login (no longer on the roster)' : 'Linked login'}
                </MenuItem>
              )}
              {loginOptions.map((option) => (
                <MenuItem key={option.userId} value={String(option.userId)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {loginError ??
                (loginOptionsReady && loginOptions.length === 0
                  ? 'No staff logins to link yet. Invite the person on Team & access first.'
                  : 'The staff login this coach signs in with.')}
            </FormHelperText>
          </FormControl>
    </>
  );
  const serverAlert = serverError && (
    <Alert severity="error" role="alert">
      {serverError}
    </Alert>
  );

  if (embedded) {
    return (
      <Stack spacing={2.5} sx={{ pt: 0.5 }}>
        {embedded.section === 'basics' ? (
          <>
            {nameFields}
            {contactFields}
            {loginField}
          </>
        ) : (
          locationFields
        )}
        {serverAlert}
      </Stack>
    );
  }

  return (
    <>
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit staff member' : 'Add staff member'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {nameFields}
          {contactFields}
          {locationFields}
          {loginField}
          {serverAlert}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save staff member' : 'Add staff member'}
        </Button>
      </DialogActions>
    </Dialog>
    <PlanLimitDialog
      open={planLimit !== null}
      info={planLimit}
      onClose={() => setPlanLimit(null)}
    />
    </>
  );
}
